import './styles/reviewsTable.scss';
import 'primeicons/primeicons.css';


import React, {ChangeEventHandler, MouseEventHandler, useMemo} from 'react';
import {DataTable, DataTablePageEvent, DataTableRowClickEvent, DataTableStateEvent} from 'primereact/datatable';
import {Column, ColumnBodyOptions} from 'primereact/column';
import {ColumnGroup} from 'primereact/columngroup';
import {Row} from 'primereact/row';
import {InputText} from 'primereact/inputtext';
import BolRating from '../../bolRating/bolRating';
import {AccessKey, ReviewTableProduct} from '../../../util/interfaces';
import {AwesomeIcon} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import useRight from '../../misc/useRight';
import IDate from '@dvrd/idate';

interface Props {
    onSort: (evt: DataTableStateEvent) => void;
    onChangeSearch: ChangeEventHandler<HTMLInputElement>;
    onChangePage: (evt: DataTablePageEvent) => void;
    onClickRow: (evt: DataTableRowClickEvent) => void;
    onClickCell: (ean: string, type: 'positive' | 'negative') => MouseEventHandler;
    products: ReviewTableProduct[];
    total: number;
    firstRow: number;
    loading: boolean;
    sortField: string;
    sortOrder: 0 | 1 | -1 | null | undefined;
    search: string;
    limit: number;
    fromDate: IDate | null;
    toDate: IDate | null;
}

export default function ReviewsTable(props: Props) {
    const [canView] = useRight(AccessKey.RATING_VIEW);
    const {
        products, loading, onSort, limit, sortField, sortOrder, firstRow, search, total, onChangePage,
        onChangeSearch, onClickRow, onClickCell, fromDate, toDate
    } = props;
    const headerDateLabel = useMemo(() => {
        if (!fromDate || !toDate) return '(Gehele periode)';
        const format = 'D MMM'
        return `(${fromDate?.format(format) ?? '-'} t/m ${toDate?.format(format) ?? '-'})`;
    }, [fromDate, toDate])

    function renderImage(product: ReviewTableProduct) {
        return <img className='image-container' src={product.image_url} alt=''/>;
    }

    function renderRating(product: ReviewTableProduct) {
        const score = product.score_rating;
        return <div className='rating-cell'>{score} <BolRating value={product.score_rating}/></div>;
    }

    function renderTitle(product: ReviewTableProduct) {
        return (
            <>
                <span title={product.title}>{product.title}<br/></span>
                <span className='ean-style'>EAN: {product.ean}</span>
            </>
        );
    }

    function renderHeader() {
        return (
            <div className='table-header'>
                <span className='p-input-icon-left'>
                    <i className='pi pi-search'/>
                    <InputText value={search} onChange={onChangeSearch} placeholder='Zoeken'/>
                </span>
            </div>


        );
    }

    function renderClickableCell(product: ReviewTableProduct, options: ColumnBodyOptions) {
        const {field} = options;
        const value = product[field as keyof ReviewTableProduct];
        const withArrows = ['period_negative', 'period_positive'].includes(field) && value !== 0;
        let clickType: 'positive' | 'negative';
        let caretColor: string;
        if (field.endsWith('positive')) {
            clickType = 'positive';
            caretColor = 'green';
        } else {
            clickType = 'negative';
            caretColor = 'red';
        }
        return (
            <div className={classNames('clickable-cell', !canView && 'cursor-default')}
                 onClick={canView ? onClickCell(product.ean, clickType) : undefined}>
                <span>{withArrows &&
                    <><AwesomeIcon name='caret-up'
                                   className={classNames('caret-icon', caretColor)}/>&nbsp;</>}{value || '-'}</span>
                {canView && <AwesomeIcon name='arrow-up-right-from-square' className='link-icon'/>}
            </div>
        );
    }

    function renderDashCell(product: ReviewTableProduct, options: ColumnBodyOptions) {
        const value = product[options.field as keyof ReviewTableProduct];
        return (
            <div className={classNames('clickable-cell', !canView && 'cursor-default')}>
                <span>{value || '-'}</span>
            </div>
        );
    }

    function renderHeaderGroup() {
        return (
            <ColumnGroup>
                <Row>
                    <Column header='' colSpan={2}/>
                    <Column header='Totaal reviews' colSpan={3}/>
                    <Column header={`Periode reviews ${headerDateLabel}`} colSpan={3}/>
                    <Column header='Score' colSpan={2}/>
                </Row>
                <Row>
                    <Column field='image_url' header='' body={renderImage}
                            style={{padding: '0 .5rem', width: 'unset'}}/>
                    <Column field='title' sortable header='Naam' body={renderTitle} style={{maxWidth: '15rem'}}/>
                    <Column field='total' sortable header='Totaal'/>
                    <Column field='total_positive' sortable header='Positief'/>
                    <Column field='total_negative' sortable header='Negatief'/>
                    <Column field='period_total' sortable header='Totaal'/>
                    <Column field='period_positive' sortable header='Positief'/>
                    <Column field='period_negative' sortable header='Negatief'/>
                    <Column field='rating' sortable header='Rating'/>
                </Row>
            </ColumnGroup>
        );
    }

    return (
        <div className='reviews-table-container'>
            <DataTable value={products} lazy onPage={onChangePage} headerColumnGroup={renderHeaderGroup()}
                       totalRecords={total} header={renderHeader()} paginator rows={limit} loading={loading}
                       globalFilterFields={['name']} showGridlines rowsPerPageOptions={[1, 5, 10, 25, 50]}
                       first={firstRow} onRowClick={canView ? onClickRow : undefined} selectionMode='single'
                       onSort={onSort} className={classNames('reviews-table', !canView && 'cursor-default')}
                       sortField={sortField} sortOrder={sortOrder} defaultSortOrder={-1}
                       emptyMessage='Data aan het verzamelen, kom morgen terug...' scrollable>
                <Column field='image_url' header='' body={renderImage}
                        style={{padding: '0 .5rem', width: 'unset'}}/>
                <Column field='title' header='Naam' body={renderTitle}
                        style={{maxWidth: '15rem', minWidth: '15rem'}}/>
                <Column field='total_reviews' header='Totaal' body={renderDashCell}/>
                <Column field='total_positive' header='Positief' style={{padding: 0}} body={renderClickableCell}/>
                <Column field='total_negative' header='Negatief' style={{padding: 0}} body={renderClickableCell}/>
                <Column field='period_total' header='Periode' body={renderDashCell}/>
                <Column field='period_positive' header='Positief' style={{padding: 0}} body={renderClickableCell}/>
                <Column field='period_negative' header='Negatief' style={{padding: 0}} body={renderClickableCell}/>
                <Column field='score_rating' header='Rating' body={renderRating}/>
                {/*<Column field='score_rating' body={renderRating}/>*/}
            </DataTable>
        </div>
    )
}